import { useRef, useState, useEffect, useCallback } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * Ambient soundscape (Web Audio, no external asset).
 * Scene A (hero): ocean/beach swell. Scene B (scrolled): nature + Ayurvedic drone.
 * On by default; audio unlocks on the first user gesture (browser autoplay policy),
 * with an off toggle. Guards against the first-click starting AND muting at once.
 */
export default function AmbientSound() {
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const oceanRef = useRef(null);
  const natureRef = useRef(null);
  const birdTimer = useRef(null);
  const sceneRef = useRef("ocean");
  const startedRef = useRef(false);
  const lastStartRef = useRef(0);
  const [on, setOn] = useState(true);
  const [running, setRunning] = useState(false);

  const scheduleBird = useCallback((ctx, dest) => {
    const chirp = () => {
      if (!ctx || ctx.state === "closed") return;
      const now = ctx.currentTime;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      const base = 1700 + Math.random() * 1600;
      o.type = "sine";
      o.frequency.setValueAtTime(base, now);
      o.frequency.exponentialRampToValueAtTime(base * 1.5, now + 0.08);
      o.frequency.exponentialRampToValueAtTime(base * 0.9, now + 0.18);
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.05, now + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
      o.connect(g); g.connect(dest);
      o.start(now); o.stop(now + 0.25);
      birdTimer.current = setTimeout(chirp, 2500 + Math.random() * 6000);
    };
    birdTimer.current = setTimeout(chirp, 2000 + Math.random() * 3000);
  }, []);

  const applyScene = useCallback((instant = false) => {
    const ctx = ctxRef.current;
    if (!ctx || !oceanRef.current) return;
    const t = ctx.currentTime + (instant ? 0.01 : 2.5);
    const nature = sceneRef.current === "nature";
    oceanRef.current.gain.cancelScheduledValues(ctx.currentTime);
    natureRef.current.gain.cancelScheduledValues(ctx.currentTime);
    oceanRef.current.gain.linearRampToValueAtTime(nature ? 0.12 : 1, t);
    natureRef.current.gain.linearRampToValueAtTime(nature ? 1 : 0, t);
  }, []);

  const fadeMaster = useCallback((target, secs) => {
    const ctx = ctxRef.current; if (!ctx) return;
    masterRef.current.gain.cancelScheduledValues(ctx.currentTime);
    masterRef.current.gain.linearRampToValueAtTime(target, ctx.currentTime + secs);
  }, []);

  const build = useCallback(() => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    ctxRef.current = ctx;

    const master = ctx.createGain(); master.gain.value = 0; master.connect(ctx.destination);
    masterRef.current = master;
    const oceanBus = ctx.createGain(); oceanBus.gain.value = 1; oceanBus.connect(master);
    const natureBus = ctx.createGain(); natureBus.gain.value = 0; natureBus.connect(master);
    oceanRef.current = oceanBus; natureRef.current = natureBus;

    const size = 2 * ctx.sampleRate;
    const buffer = ctx.createBuffer(1, size, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < size; i++) { const w = Math.random() * 2 - 1; last = (last + 0.02 * w) / 1.02; data[i] = last * 3.2; }

    const oNoise = ctx.createBufferSource(); oNoise.buffer = buffer; oNoise.loop = true;
    const oLp = ctx.createBiquadFilter(); oLp.type = "lowpass"; oLp.frequency.value = 600;
    const oGain = ctx.createGain(); oGain.gain.value = 0.42;
    const lfo = ctx.createOscillator(); lfo.frequency.value = 0.08;
    const lfoGain = ctx.createGain(); lfoGain.gain.value = 0.25;
    lfo.connect(lfoGain); lfoGain.connect(oGain.gain);
    oNoise.connect(oLp); oLp.connect(oGain); oGain.connect(oceanBus);

    const wNoise = ctx.createBufferSource(); wNoise.buffer = buffer; wNoise.loop = true;
    const wBp = ctx.createBiquadFilter(); wBp.type = "bandpass"; wBp.frequency.value = 1200; wBp.Q.value = 0.7;
    const wGain = ctx.createGain(); wGain.gain.value = 0.16;
    const wLfo = ctx.createOscillator(); wLfo.frequency.value = 0.16;
    const wLfoGain = ctx.createGain(); wLfoGain.gain.value = 0.09;
    wLfo.connect(wLfoGain); wLfoGain.connect(wGain.gain);
    wNoise.connect(wBp); wBp.connect(wGain); wGain.connect(natureBus);

    [130.81, 196.0, 261.63, 392.0].forEach((f, idx) => {
      const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = f;
      const g = ctx.createGain(); g.gain.value = 0.03 / (idx + 1);
      const trem = ctx.createOscillator(); trem.frequency.value = 0.05 + idx * 0.018;
      const tg = ctx.createGain(); tg.gain.value = 0.012;
      trem.connect(tg); tg.connect(g.gain);
      o.connect(g); g.connect(idx >= 2 ? natureBus : master);
      o.start(); trem.start();
    });

    oNoise.start(); lfo.start(); wNoise.start(); wLfo.start();
    scheduleBird(ctx, natureBus);
    startedRef.current = true;
    applyScene(true);
  }, [scheduleBird, applyScene]);

  const startAudio = useCallback(async () => {
    if (!startedRef.current) build();
    try { await ctxRef.current.resume(); } catch {}
    lastStartRef.current = Date.now();
    fadeMaster(on ? 0.62 : 0, 3.2);
    if (ctxRef.current?.state === "running" && on) setRunning(true);
  }, [build, fadeMaster, on]);

  // Unlock audio on the first user interaction (autoplay policy).
  // Listen broadly (incl. scroll/wheel) and keep listeners until the
  // context is actually running, so scroll-first visitors still get sound.
  useEffect(() => {
    if (!on) return;
    const evts = ["pointerdown", "mousedown", "click", "keydown", "touchstart", "touchend", "wheel", "scroll"];
    const handler = async () => {
      await startAudio();
      if (ctxRef.current?.state === "running") remove();
    };
    const remove = () => evts.forEach((e) => window.removeEventListener(e, handler));
    evts.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    return remove;
  }, [on, startAudio]);

  // Scroll scene crossfade
  useEffect(() => {
    const onScroll = () => {
      const scene = window.scrollY > window.innerHeight * 0.65 ? "nature" : "ocean";
      if (scene !== sceneRef.current) { sceneRef.current = scene; if (startedRef.current && on) applyScene(); }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [applyScene, on]);

  const toggle = async () => {
    if (!startedRef.current) { setOn(true); await startAudio(); return; }
    if (on) {
      // Ignore the same click that just unlocked audio (prevents start-then-mute)
      if (Date.now() - lastStartRef.current < 700) return;
      setOn(false); setRunning(false); fadeMaster(0, 1.2);
    } else {
      setOn(true); try { await ctxRef.current.resume(); } catch {} setRunning(true); fadeMaster(0.62, 2);
    }
  };

  const label = !on ? "Sound Off" : running ? "Sound On" : "Tap for Sound";

  return (
    <button onClick={toggle} data-testid="ambient-sound-toggle"
      className={`fixed bottom-24 left-5 md:bottom-6 md:left-6 z-[55] flex items-center gap-2.5 pl-3.5 pr-4 py-2.5 rounded-full glass-nav text-rr-ivory transition-colors ${on && !running ? "border-rr-gold shadow-[0_0_28px_rgba(201,164,107,0.35)]" : "hover:border-rr-gold"}`}>
      <span className={`relative flex items-center justify-center w-6 h-6 ${on ? "text-rr-gold" : "text-rr-muted"}`}>
        {on ? <Volume2 className="w-4.5 h-4.5" strokeWidth={1.5} /> : <VolumeX className="w-4.5 h-4.5" strokeWidth={1.5} />}
        {on && <span className="absolute inset-0 rounded-full border border-rr-gold/50 animate-ping" />}
      </span>
      <span className="text-[10px] uppercase tracking-[0.2em] text-rr-ivory/70 hidden sm:inline">{label}</span>
    </button>
  );
}
